/**
 * Mock de "Eau à 3 mois" : sert les données de l'app Shiny
 * (data_app_cours_de_campagne, converties en JSON dans ./data_eau3mois/) en
 * attendant les routes RPlumber. Chaque fonction renvoie le contrat que la
 * vraie route devra respecter ; les fichiers JSON sont chargés à la demande
 * (un scénario = un fichier), jamais tous ensemble.
 *
 * Contrat commun des paramètres :
 *   { type: 'culture'|'elevage', idProduction, semis, alimentation, abreuvement,
 *     rcp: '4.5'|'8.5', grandeur: 'culture'|'irrigation', coupure: 'AAAA-MM-JJ' }
 */
import { scenarioDepuisProduction, coefficientGrandeur, formatJJMMAAAA, ajouterJours } from "../utils/eau3mois.js";

const charger = (nom) => import(`./data_eau3mois/${nom}.json`).then((m) => m.default);

// Historique SAFRAN (07_historique_safran.R) : un fichier par (type de maïs x date
// de semis), contenant le cumul territorial du besoin en eau de chaque année
// observée. La page affiche les 4 combinaisons déjà couvertes par les scénarios du
// Shiny (COMBINAISONS du script), dans l'ordre du panneau 2x2 : les semis sont
// triés par libellé, donc 05/05 avant 15/04.
const chargerHistorique = (nom) => import(`./data_historique_safran/${nom}.json`).then((m) => m.default);
const COMBINAISONS_HISTORIQUE = [
  { token: "Mais P", semis: "05-05", systeme: "Maïs Précoce" },
  { token: "Mais P", semis: "04-15", systeme: "Maïs Précoce" },
  { token: "Mais T", semis: "05-05", systeme: "Maïs Tardif" },
  { token: "Mais T", semis: "04-15", systeme: "Maïs Tardif" },
];
const ANNEE_EN_COURS = 2026;

// Composition du total territoire (COMPOSITION_TOTAL du Shiny) : [système,
// modalité, libellé du poste]. Les résidents et le tourisme n'ont pour l'instant
// de données que jusqu'au 1er septembre : pour les coupures suivantes le total ne
// peut pas être calculé, et la page le dit au lieu de masquer ces dates.
const COMPOSITION_TOTAL = [
  ["Maïs Précoce", "04-15", "Maïs"],
  ["Vache laitière", "Ensilage mais", "Vaches laitières"],
  ["Porc à l'engraissement", "Sec-Bol", "Porcs engraissement"],
  ["Touristes", "Centrale", "Tourisme"],
  ["Résidents", "Centrale", "Résidents"],
];

const donneesAbsentes = (coupure) =>
  new Error(`Les données de la coupure du ${formatJJMMAAAA(coupure)} ne sont pas disponibles.`);

const mettreAEchelle = (tableau, k) => (k === 1 ? tableau : tableau.map((v) => Number((v * k).toPrecision(6))));
const mettreBandesAEchelle = (bandes, k) =>
  Object.fromEntries(Object.entries(bandes).map(([nom, t]) => [nom, mettreAEchelle(t, k)]));

/**
 * "Réalisé" SAFRAN d'un système : partie OBSERVÉE du scénario à la coupure la
 * plus tardive disponible (serie_realisee du Shiny) — lue dans les fichiers de
 * scénarios eux-mêmes, pas dans un fichier à part : un fichier séparé pouvait
 * rester sur d'anciennes données (réalisé arrêté au 1er septembre) alors que
 * les scénarios étaient à jour.
 */
async function serieRealisee(index, systeme, modalite) {
  const dernier = index
    .filter((l) => l.systeme === systeme && l.modalite === modalite)
    .sort((a, b) => (a.coupure < b.coupure ? 1 : -1))[0];
  if (!dernier) return null;
  const s = await charger(dernier.cle);
  const rc = s.rcp["8.5"] ?? Object.values(s.rcp)[0];
  return {
    origine: s.origine, coupure: s.coupure,
    journalier: rc.journalier.med.slice(0, rc.nObs),
    cumul: rc.cumul.med.slice(0, rc.nObs),
  };
}

async function ouvrirScenario(params) {
  const { systeme, modalite, famille } = scenarioDepuisProduction(params);
  const index = await charger("index");
  const ligne = index.find(
    (l) => l.systeme === systeme && l.modalite === modalite && l.coupure === params.coupure
  );
  if (!ligne) throw donneesAbsentes(params.coupure);
  const scenario = await charger(ligne.cle);
  const rcp = scenario.rcp[params.rcp];
  if (!rcp) throw new Error(`Pas de trajectoires pour le RCP ${params.rcp}`);
  const realise = await serieRealisee(index, systeme, modalite);
  const k = coefficientGrandeur(params.type, params.grandeur);
  const info = {
    systeme, modalite, famille,
    unite: scenario.unite, quantite: scenario.quantite, uniteQuantite: scenario.uniteQuantite,
    nMailles: scenario.nMailles, nTraj: rcp.nTraj, annees: rcp.annees,
    nCombinaisons: Math.round(rcp.nTraj / rcp.annees.length),
  };
  return { scenario, rcp, realise, k, info };
}

export const mockEau3Mois = {
  /**
   * Coupures proposées : les MÊMES sur toutes les pages (celles du jeu livré
   * pour le système choisi ; pour le total, celles de la composition).
   */
  async options(params) {
    const index = await charger("index");
    const coupuresDe = (systeme, modalite) =>
      index.filter((l) => l.systeme === systeme && l.modalite === modalite).map((l) => l.coupure);
    const brut = params.total
      ? COMPOSITION_TOTAL.flatMap(([s, m]) => coupuresDe(s, m))
      : (() => { const { systeme, modalite } = scenarioDepuisProduction(params); return coupuresDe(systeme, modalite); })();
    return { coupures: [...new Set(brut)].sort() };
  },

  /**
   * Historique du besoin en eau du maïs : pour chacune des 4 combinaisons (précoce
   * et tardif x semis 05/05 et 15/04), une courbe de cumul (millions de m³) par
   * année SAFRAN observée, plus l'année en cours (partie observée du scénario à la
   * coupure la plus tardive). Aucun paramètre : ce sont des données observées, sans
   * scénario climatique, grandeur ni coupure.
   */
  async historique() {
    const index = await charger("index");
    const panneaux = [];
    let surfaceHa = null;
    let nMailles = null;
    for (const { token, semis, systeme } of COMBINAISONS_HISTORIQUE) {
      const h = await chargerHistorique(`${token}_${semis}`.replace(/[^A-Za-z0-9]+/g, "_"));
      surfaceHa = h.surfaceHa;
      nMailles = h.nMailles;
      const courbes = { ...h.courbes };
      let finObserve = null;
      const realise = await serieRealisee(index, systeme, semis);
      if (realise?.cumul?.length) {
        courbes[String(ANNEE_EN_COURS)] = realise.cumul;
        finObserve = ajouterJours(realise.origine, realise.cumul.length - 1);
      }
      panneaux.push({
        label: `${systeme}, semis ${semis.slice(3)}/${semis.slice(0, 2)}`,
        origine: `${ANNEE_EN_COURS}-${semis}`,
        nJours: h.nJours,
        courbes,
        anneeMiseEnAvant: finObserve ? ANNEE_EN_COURS : null,
        finObserve,
        nonProlongees: h.nonProlongees,
      });
    }
    return { info: { surfaceHa, nMailles }, anneeMiseEnAvant: ANNEE_EN_COURS, panneaux };
  },

  /** Besoin journalier : enveloppe min / q25 / médiane / q75 / max + réalisé SAFRAN. */
  async journalier(params) {
    const { scenario, rcp, realise, k, info } = await ouvrirScenario(params);
    return {
      info, origine: scenario.origine, coupure: scenario.coupure, nObs: rcp.nObs,
      bandes: mettreBandesAEchelle(rcp.journalier, k),
      realise: realise && { origine: realise.origine, coupure: realise.coupure, valeurs: mettreAEchelle(realise.journalier, k) },
    };
  },

  /** Cumul territorial (millions de m³) : cumul par trajectoire PUIS quantiles. */
  async cumul(params) {
    const { scenario, rcp, realise, k, info } = await ouvrirScenario(params);
    return {
      info, origine: scenario.origine, coupure: scenario.coupure, nObs: rcp.nObs,
      bandes: mettreBandesAEchelle(rcp.cumul, k),
      realise: realise && { origine: realise.origine, coupure: realise.coupure, valeurs: mettreAEchelle(realise.cumul, k) },
    };
  },

  /** Cumul de chaque trajectoire, pour chaque jour de la coupure au 15/10. */
  async distribution(params) {
    const { scenario, rcp, k, info } = await ouvrirScenario(params);
    const { j0, valeurs } = rcp.distribution;
    return {
      info, origine: scenario.origine, coupure: scenario.coupure, j0,
      valeurs: k === 1 ? valeurs : valeurs.map((ligne) => mettreAEchelle(ligne, k)),
    };
  },

  /** Total du territoire : 5 postes sommés trajectoire par trajectoire. */
  async total(params) {
    // Postes réellement utilisés (sans les résidents si on les masque) qui n'ont
    // pas de données pour cette coupure : on le dit, plutôt qu'un total faux.
    const index = await charger("index");
    const manquants = COMPOSITION_TOTAL
      .filter(([, , poste]) => !(params.masquerResidents && poste === "Résidents"))
      .filter(([s, m]) => !index.some((l) => l.systeme === s && l.modalite === m && l.coupure === params.coupure))
      .map(([, , poste]) => poste);
    if (manquants.length) {
      throw new Error(
        `Total indisponible pour la coupure du ${formatJJMMAAAA(params.coupure)} : pas encore de données pour ${manquants.join(" et ")}.`
      );
    }
    let t;
    try {
      t = await charger(`total_${params.coupure.replace(/-/g, "")}`);
    } catch (e) {
      throw donneesAbsentes(params.coupure);
    }
    const variante = t.rcp[params.rcp]?.[`${params.grandeur === "irrigation" ? "irrigation" : "culture"}|${params.masquerResidents ? "sans" : "avec"}`];
    if (!variante) throw new Error("Total indisponible pour ces paramètres");
    return { coupure: t.coupure, origine: t.origine, nJours: t.nJours, ...variante };
  },
};
